import axios from "axios";
import { STORAGE_KEYS } from "../constants";
import { isTokenExpired } from "../utils/tokenUtils";
import { authService } from "../services/authService";
import { store } from "../store/store";
import { tokenRefreshed, logout } from "../store/slices/authSlice";
import { clearAuthData } from "../utils/authSync";

const api = axios.create({
  baseURL:
    process.env.REACT_APP_API_BASE_URL ||
    "https://web-app-win-dev.azurewebsites.net",
  headers: {
    "Content-Type": "application/json",
  },
  validateStatus: (status) => status >= 200 && status < 300,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add authorization token here
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token) {
      try {
        const tokenData = JSON.parse(token);

        // Check if token is expired before making the request
        if (isTokenExpired(tokenData)) {
          console.warn("Token is expired, removing from localStorage");
          localStorage.removeItem(STORAGE_KEYS.TOKEN);
          // Don't add Authorization header for expired tokens
          return config;
        }

        config.headers.Authorization = `Bearer ${tokenData.access_token}`;
      } catch (error) {
        console.error("Invalid token format:", error);
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      if (token) {
        try {
          const tokenData = JSON.parse(token);

          // Try to refresh the token
          if (tokenData.refresh_token) {
            console.log("Attempting to refresh token due to 401 error");
            const newToken = await authService.refreshToken({
              access_token: tokenData.access_token,
              refresh_token: tokenData.refresh_token,
            });

            // IMPORTANT: Backend returns BOTH new access_token AND new refresh_token
            // We MUST save the new refresh_token, otherwise backend will treat it as an attack
            const newTokenWithTimestamp = {
              ...newToken, // This includes the NEW refresh_token from response
              issued_at: Date.now() / 1000,
            };

            console.log(
              "✓ Received new token pair (access + refresh) from backend"
            );

            // Save new token to localStorage
            localStorage.setItem(
              STORAGE_KEYS.TOKEN,
              JSON.stringify(newTokenWithTimestamp)
            );

            // Update Redux state with new token
            store.dispatch(tokenRefreshed({ token: newTokenWithTimestamp }));

            console.log(
              "✓ Token refreshed successfully in axios interceptor, new tokens saved"
            );

            // Update the authorization header and retry the request
            originalRequest.headers.Authorization = `Bearer ${newToken.access_token}`;
            return api(originalRequest);
          }
        } catch (refreshError: unknown) {
          console.error(
            "Token refresh failed in axios interceptor:",
            refreshError
          );

          // Any error during refresh should logout the user
          // BUT skip logout if user is in registration flow
          const isRegistrationFlow = localStorage.getItem(
            STORAGE_KEYS.REGISTRATION_TOKEN
          );
          const wasAuthenticated = !!token; // User had a token before

          if (!isRegistrationFlow && wasAuthenticated) {
            console.warn("Logging out user due to token refresh failure");
            clearAuthData();
            store.dispatch(logout());
          } else if (!wasAuthenticated) {
            console.log(
              "User not yet authenticated, skipping logout on refresh failure"
            );
          }
          return Promise.reject(refreshError);
        }
      }

      // If no refresh token available, logout the user
      // BUT skip logout if user is in registration flow OR not yet authenticated
      const isRegistrationFlow = localStorage.getItem(
        STORAGE_KEYS.REGISTRATION_TOKEN
      );
      const wasAuthenticated = !!token; // User had a token before

      if (!isRegistrationFlow && wasAuthenticated) {
        console.warn("No refresh token available, logging out user");
        clearAuthData();
        store.dispatch(logout());
      } else if (!wasAuthenticated) {
        console.log("User not yet authenticated, skipping logout on 401");
      }
    }

    return Promise.reject(error);
  }
);

export default api;
